"use client"
import { addressData } from '@/redux/features/addressSlice'
import { cartData, setCartCount } from '@/redux/features/cartSlice'
import { addUser, setSessionId } from '@/redux/features/userSlice'
import { _projectName_ } from '@/utils/helper/AllStaticVariables/configVariables'
import { useGoogleLogin } from '@react-oauth/google'
import axios from 'axios'
import { Form, Formik } from 'formik'
import Link from 'next/link'
import { setCookie } from 'nookies'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import * as Yup from 'yup'
import { focusInput, generatePassword } from '../../lib/AllGlobalFunction'
import axiosProvider from '../../lib/AxiosProvider'
import {
  getBaseUrl,
  getDeviceId,
  getRefreshToken,
  getUserToken,
  showToast
} from '../../lib/GetBaseUrl'
import { _emailRegex_, _phoneNumberRegex_ } from '../../lib/Regex'
import { _exception } from '../../lib/exceptionMessage'
import Loader from '../Loader'
import InputComponent from '../base/InputComponent'
import ModalComponent from '../base/ModalComponent'
import PasswordStrengthCheck from '../base/PasswordStrengthCheck'
import FacebookLogin from './FacebookLogin'
import MobileNoGoogleModel from './MobileNoGoogleModel'
import { setWishlist } from '@/redux/features/wishlistSlice'

const Login = ({
  LoginOTPForm,
  isLogin,
  handleForgot,
  toggleForm,
  onClose,
  setIsVerifyNumber,
  isVerifyNumber
}) => {
  const baseUrl = getBaseUrl()
  const userToken = getUserToken()
  const [loading, setLoading] = useState(false)
  const dispatch = useDispatch()
  const deviceId = getDeviceId()
  const initialValues = {
    userName: '',
    password: '',
    deviceId: deviceId
  }
  const accessToken = getRefreshToken()
  const [cartId, setCartId] = useState()
  const sessionId = useSelector((state) => state?.user?.sessionId)
  const isPhoneNumber = (value) => /^\d+$/.test(value)
  const { cart } = useSelector((state) => state?.cart)

  const getCartCount = async (userId) => {
    try {
      if (userId?.length) {
        const res = await axiosProvider({
          method: 'GET',
          endpoint: `Cart/bysessionId?sessionId=${userId}`
        })
        dispatch(setCartCount(res?.data?.pagination?.recordCount))
      }
    } catch (error) {
      showToast(dispatch, {
        data: { code: 204, message: _exception?.message }
      })
    }
  }

  const validationSchema = Yup.object().shape({
    userName: Yup.string()
      .test(
        'valid-login',
        'Please enter a valid email address or phone number',
        (value) => {
          return _emailRegex_.test(value) || _phoneNumberRegex_.test(value)
        }
      )
      .required('Email or phone number is required'),
    password: Yup.string()
      .required('Password field is required')
      .test('Password is required', (value) => !value.includes(' '))
  })

  const fetchGoogleUserInfo = async (accessToken) => {
    return axios.get(`https://www.googleapis.com/oauth2/v1/userinfo`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Accept: 'application/json'
      }
    })
  }

  const fetchEmailCheck = async (emailId) => {
    return axios.get(`${baseUrl}Account/Customer/ByEmail?EmailId=${emailId}`, {
      headers: {
        Authorization: `Bearer ${userToken}`,
        device_id: deviceId
      }
    })
  }

  const postLoginViaEmail = async (loginData) => {
    return axios.post(`${baseUrl}Account/Customer/LoginViaEmail`, loginData, {
      headers: {
        'Content-Type': 'application/json',
        accept: '*/*'
      }
    })
  }

  const postSignUp = async (values) => {
    setLoading(true)
    try {
      const response = await axios.post(
        `${baseUrl}Account/Customer/signUp`,
        JSON.stringify(values),
        {
          headers: {
            'Content-Type': 'application/json',
            accept: '*/*'
          }
        }
      )
      if (response?.data?.code === 200) {
        await handleAuthResponse(values)
      } else {
        setIsVerifyNumber({ show: false, data: null })
        showToast(dispatch, response)
      }
    } catch (error) {
      showToast(dispatch, {
        data: { code: 204, message: error.message }
      })
    }
    setLoading(false)
  }

  const handleAuthResponse = async (loginResponse) => {
    const data = JSON.stringify({
      emailId: loginResponse?.emailID
        ? loginResponse?.emailID
        : loginResponse?.data?.email.trim(),
      deviceId: deviceId
    })

    setLoading(true)

    try {
      const call = await postLoginViaEmail(data)
      setLoading(false)

      if (call?.data?.code === 200) {
        setTimeout(() => {
          dispatch(
            addUser({
              user: call?.data?.currentUser,
              userToken: call?.data?.tokens?.accessToken,
              refreshToken: call?.data?.tokens?.refreshToken,
              deviceId: deviceId
            })
          )
          setCookie(null, 'userToken', call.data.tokens.accessToken, {
            path: '/',
            maxAge: 30 * 24 * 60 * 60
          })
          setCookie(null, 'refreshToken', call?.data?.tokens?.refreshToken, {
            path: '/',
            maxAge: 30 * 24 * 60 * 60
          })
        }, [1500])

        showToast(dispatch, call)
      } else {
        showToast(dispatch, {
          data: { code: 204, message: 'Authentication failed' }
        })
      }
    } catch (error) {
      setLoading(false)
      showToast(dispatch, {
        data: { code: 204, message: error.message }
      })
    }
  }

  const handleGoogleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      const googleRes = await fetchGoogleUserInfo(tokenResponse?.access_token)
      const checkEmail = await fetchEmailCheck(googleRes?.data?.email)

      if (checkEmail?.data?.code === 200) {
        await handleAuthResponse(googleRes)
      } else if (checkEmail?.data?.code === 204) {
        const signUpData = {
          firstName: googleRes?.data?.given_name?.trim(),
          lastName: googleRes?.data?.family_name?.trim(),
          emailID: googleRes?.data?.email?.trim(),
          MobileNo: '',
          password: generatePassword(8),
          gender: '',
          deviceId: getDeviceId()
        }
        // onClose()
        // setModelShow({ show: true, data: signUpData })
        setIsVerifyNumber({ show: true, data: signUpData })
      } else {
        showToast(dispatch, {
          data: { code: 204, message: 'Email check failed' }
        })
      }
    }
  })

  const onSubmit = async (values, { resetForm }) => {
    if (accessToken === null) {
      try {
        setLoading(true)
        const response = await axiosProvider({
          method: 'POST',
          endpoint: 'Account/Customer/Login',
          data: values
        })
        setLoading(false)

        if (response?.data?.code === 200) {
          let userId = response?.data?.currentUser?.userId

          setCookie(null, 'userToken', response?.data?.tokens?.accessToken, {
            path: '/',
            maxAge: 30 * 24 * 60 * 60
          })

          setCookie(
            null,
            'refreshToken',
            response?.data?.tokens?.refreshToken,
            { path: '/', maxAge: 30 * 24 * 60 * 60 }
          )
          // Implementation of session id login here
          const headers = {
            Authorization: `Bearer ${response?.data?.tokens?.accessToken}`,
            device_id: deviceId ? deviceId : nextCookies.get('deviceId')?.value
          }

          if (sessionId) {
            const responseSession = await axiosProvider({
              method: 'PUT',
              endpoint: 'Cart/UpdateSession',
              queryString: `?UserId=${userId}&SessionId=${sessionId}&CartId=${
                cartId ? cartId : 0
              }`,
              headers
            })
            if (responseSession?.status === 200) {
              dispatch(setSessionId(userId))
            }
          }

          dispatch(
            addUser({
              user: response?.data?.currentUser,
              userToken: response?.data?.tokens?.accessToken,
              refreshToken: response?.data?.tokens?.refreshToken,
              deviceId: getDeviceId()
            })
          )
          dispatch(setSessionId(userId))
          setCookie(null, 'userId', userId, {
            path: '/',
            maxAge: 30 * 24 * 60 * 60
          })
          getCartCount(response?.data?.currentUser?.userId)
          localStorage.removeItem('hk-compare-data')

          resetForm({ values: '' })

          // Handle Addresss api
          const responseAddress = await axiosProvider({
            method: 'GET',
            endpoint: 'Address/byUserId',
            queryString: `?userId=${userId}`
          })
          if (responseAddress?.data?.code === 200) {
            const setDefaultAddress =
              responseAddress?.data?.data?.length > 0 &&
              responseAddress?.data?.data?.find((item) => item?.setDefault)

            if (responseAddress?.data?.data?.length === 1) {
              dispatch(
                cartData({
                  ...cart,
                  deliveryData: responseAddress?.data?.data[0]
                })
              )
            } else if (setDefaultAddress?.id) {
              dispatch(cartData({ ...cart, deliveryData: setDefaultAddress }))
            }

            dispatch(addressData(responseAddress?.data?.data))
          }

          const responseWishlist = await axiosProvider({
            method: 'GET',
            endpoint: 'Wishlist/byUserId',
            queryString: `?userId=${userId}&pageIndex=0&pageSize=0`
          })
          if (responseWishlist?.data?.code === 200) {
            const wishlistIds = responseWishlist?.data?.data?.map(
              (item) => item?.products?.guid
            )
            dispatch(setWishlist(wishlistIds))
          }

          onClose()
        }
        showToast(dispatch, response)
      } catch (error) {
        setLoading(false)
        showToast(dispatch, {
          data: { code: 204, message: _exception?.message }
        })
      }
    }
  }

  useEffect(() => {
    if (accessToken !== null) {
      onClose()
    }
  }, [accessToken])

  useEffect(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      let cartId = localStorage.getItem('cartId')
      setCartId(cartId)
    }
  }, [])

  return (
    <div>
      {loading && <Loader />}
      {!isVerifyNumber?.show && (
        <Formik
          enableReinitialize={true}
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {({ values, setFieldValue, validateForm }) => (
            <Form>
              <h2 className="login-title">
                <button
                  className={`${isLogin && 'activeblue'} loginaccount`}
                  onClick={() => toggleForm('login')}
                >
                  Login
                </button>
                /
                <button
                  className={`${!isLogin && 'activeblue'} signupaccount`}
                  onClick={() => toggleForm('signup')}
                >
                  Sign Up
                </button>
              </h2>

              <InputComponent
                labelText={'Mobile Number or Email Address'}
                id={'userName'}
                type={'text'}
                required
                labelClass={'sign-com-label'}
                maxLength={isPhoneNumber(values?.userName) ? 10 : 255}
                onChange={(e) => {
                  setFieldValue('userName', e?.target?.value)
                }}
                autoFocus
                value={values?.userName}
                name="userName"
                onBlur={(e) => {
                  let fieldName = e?.target?.name
                  setFieldValue(fieldName, values[fieldName]?.trim())
                }}
              />
              <div className="eye-main-pasw">
                <PasswordStrengthCheck
                  required={true}
                  isLogin={isLogin}
                  onChange={(e) => {
                    setFieldValue('password', e?.target?.value)
                  }}
                  id="password"
                  value={values?.password}
                  name="password"
                  onBlur={(e) => {
                    let fieldName = e?.target?.name
                    setFieldValue(fieldName, values[fieldName]?.trim())
                  }}
                />
              </div>

              <div className="forget-login">
                <Link href="#." onClick={handleForgot}>
                  Forgot Password?
                </Link>
              </div>

              <div className="btn-submit-login">
                <button
                  onClick={() => {
                    validateForm()?.then((focusError) =>
                      focusInput(Object?.keys(focusError)?.[0])
                    )
                  }}
                  type="submit"
                  className="m-btn btn-login cursor-pointer"
                >
                  Login
                </button>
                {/* <button
                  type="button"
                  className="m-btn login-otp-btn cursor-pointer"
                  onClick={() => LoginOTPForm('loginotp')}
                >
                  Login Via OTP
                </button> */}
              </div>
              {/* <div>
                <button
                  type="button"
                  onClick={(e) => {
                    e?.preventDefault()
                    // signIn('google', { redirect: false })
                    handleGoogleLogin()
                  }}
                  class="text-white bg-[#4285F4] hover:bg-[#4285F4]/90 focus:ring-4 focus:ring-[#4285F4]/50 font-medium rounded-lg w-full justify-center text-sm px-5 py-2.5 text-center inline-flex items-center dark:focus:ring-[#4285F4]/55 mr-2 mb-2"
                >
                  <svg
                    class="mr-2 -ml-1 w-4 h-4"
                    aria-hidden="true"
                    focusable="false"
                    data-prefix="fab"
                    data-icon="google"
                    role="img"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 488 512"
                  >
                    <path
                      fill="currentColor"
                      d="M488 261.8C488 403.3 391.1 504 248 504 110.8 504 0 393.2 0 256S110.8 8 248 8c66.8 0 123 24.5 166.3 64.9l-67.5 64.9C258.5 52.6 94.3 116.6 94.3 256c0 86.5 69.1 156.6 153.7 156.6 98.2 0 135-70.4 140.8-106.9H248v-85.3h236.1c2.3 12.7 3.9 24.9 3.9 41.4z"
                    ></path>
                  </svg>
                  Sign in with Google
                </button>
                {/* <FacebookLogin
                  fetchEmailCheck={fetchEmailCheck}
                  handleAuthResponse={handleAuthResponse}
                  setModelShow={setIsVerifyNumber}
                />
              </div> */}
              <div className="new-account">
                <div className="create-account-section">
                  <span> New to {_projectName_}?&nbsp;</span>
                  <Link
                    href="#."
                    className="account-link-signup"
                    onClick={() => toggleForm('signup')}
                  >
                    Create Account
                  </Link>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      )}
      <ModalComponent
        isOpen={isVerifyNumber?.show}
        modalSize={'modal-sm'}
        headClass={'HeaderText'}
        headingText={'Verify details'}
        onClose={() => {
          setIsVerifyNumber({ show: false, type: '' })
        }}
      >
        <MobileNoGoogleModel
          initialValues={isVerifyNumber?.data}
          onSubmit={postSignUp}
        />
      </ModalComponent>
    </div>
  )
}

export default Login
